/**
 * fetchAllReadings.js — Chargement complet des mesures d'une session (D235).
 *
 * Pourquoi : GET /api/sessions/:id/readings renvoie au plus 5 000 mesures par
 * défaut (10 000 sur demande). La page de session l'appelait sans pagination :
 * au-delà de 5 000 mesures, statistiques, résumé et PDF portaient sur le début
 * de la session seulement, sans aucun message.
 *
 * Choix :
 *   - pages séquentielles à la limite maximale de l'endpoint : une longue session
 *     coûte peu de requêtes, sans rafale contre l'apiLimiter (200 / 15 min) ;
 *   - plafond de sécurité côté client : au-delà, on s'arrête et on le dit
 *     (`capped`), jamais de troncature silencieuse ;
 *   - annulable (AbortSignal) : aucun résultat après un changement de session ;
 *   - une erreur sur une page fait échouer tout le chargement : jamais de
 *     mesures partielles présentées comme complètes.
 */
import { getAuthHeaders } from '../utils/authFetch';

// Maximum accepté par l'endpoint (sessionController.getReadings).
export const READINGS_PAGE_SIZE = 10000;
// ≈ 69 jours à 1 mesure / min, ≈ 1 an d'EkynAir Live à 5 min.
export const READINGS_SAFETY_CAP = 100000;

function abortError() {
  return Object.assign(new Error('aborted'), { name: 'AbortError' });
}

function throwIfAborted(signal) {
  if (signal?.aborted) throw abortError();
}

function timeOf(reading) {
  const ms = new Date(reading?.timestamp).getTime();
  return Number.isFinite(ms) ? ms : Infinity;
}

/**
 * @param {string} sessionId
 * @param {{ signal?: AbortSignal, cap?: number, pageSize?: number,
 *   pageUrl?: (limit: number, skip: number) => string, requestInit?: Object }} [options]
 *   `pageUrl` / `requestInit` : autre route de mesures, même pagination (page du
 *   lien de séance, D247 : route publique, jeton en en-tête, sans cookie).
 * @returns {Promise<{ readings: Array, total: number, capped: boolean }>}
 *   Rejette avec `name: 'AbortError'` si annulé, `status` (+ `response`) sur
 *   une réponse HTTP en erreur, `code: 'payload_invalid'` sur un corps inattendu.
 */
export async function fetchAllReadings(sessionId, options = {}) {
  const { signal, cap = READINGS_SAFETY_CAP, pageSize = READINGS_PAGE_SIZE } = options;
  const pageUrl = options.pageUrl || ((limit, skip) => `/api/sessions/${sessionId}/readings?limit=${limit}&skip=${skip}`);
  const requestInit = options.requestInit || { credentials: 'include', headers: getAuthHeaders() };
  const readings = [];
  let total = null;

  for (;;) {
    const limit = Math.min(pageSize, cap - readings.length);
    const res = await fetch(pageUrl(limit, readings.length), { ...requestInit, signal });
    throwIfAborted(signal);
    if (!res.ok) {
      throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status, response: res });
    }
    const body = await res.json();
    throwIfAborted(signal);
    if (!body || body.success !== true || !Array.isArray(body.readings)) {
      throw Object.assign(new Error('payload_invalid'), { code: 'payload_invalid' });
    }

    const page = body.readings;
    for (const r of page) readings.push(r);
    if (Number.isFinite(body.total)) total = body.total;
    const pageLimit = Number.isFinite(body.limit) ? body.limit : limit;

    if (page.length === 0) break;
    if (total !== null ? readings.length >= total : page.length < pageLimit) break;
    if (readings.length >= cap) break;
  }

  const kept = readings.slice(0, cap);
  // Sans total (réponse ancienne), un plafond atteint est signalé par prudence.
  const capped = total !== null ? total > kept.length : kept.length >= cap;
  kept.sort((a, b) => {
    const ta = timeOf(a);
    const tb = timeOf(b);
    return ta === tb ? 0 : ta < tb ? -1 : 1;
  });
  return { readings: kept, total: total ?? kept.length, capped };
}
